// baseUrl and getHeaders are explicit config here — a plain HTML page has
// neither Angular's HttpClient nor an AuthInterceptor to supply them.

import {
  KbChatRequest,
  KbChatResponse,
  KbCreateSessionRequest,
  KbCreateSessionResponse,
} from './types';

export class KbApiError extends Error {
  constructor(
    message: string,
    public readonly status?: number,
    /** Seconds to wait, from the Retry-After header on a 429. */
    public readonly retryAfter?: number,
  ) {
    super(message);
    this.name = 'KbApiError';
  }
}

export interface KbChatClientConfig {
  baseUrl: string;
  getHeaders?: () => Record<string, string> | Promise<Record<string, string>>;
}

async function request<T>(config: KbChatClientConfig, path: string, init: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((await config.getHeaders?.()) ?? {}),
  };
  let res: Response;
  try {
    res = await fetch(`${config.baseUrl}${path}`, { ...init, headers });
  } catch (err) {
    // Normalize a thrown fetch to the same error type as a non-2xx response.
    throw new KbApiError(err instanceof Error ? err.message : 'Network request failed');
  }
  if (!res.ok) {
    let detail = '';
    try {
      detail = (await res.json())?.detail ?? '';
    } catch {
      // body wasn't JSON (or was empty) — fall through with a bare status error
    }
    // Retry-After only reaches us cross-origin because the API adds it to
    // Access-Control-Expose-Headers (see app/main.py); without that the browser
    // hides it and this reads null even though curl shows the header.
    const retryAfter = Number(res.headers.get('Retry-After'));
    throw new KbApiError(
      detail || `Request failed (${res.status})`,
      res.status,
      Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    );
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export class KbChatClient {
  constructor(private config: KbChatClientConfig) {}

  /** Open a session. In 'support' mode the backend takes the tenant from the
   * verified portal identity, so there is nothing tenant-related to send. */
  createSession(mode: KbCreateSessionRequest['mode']): Promise<KbCreateSessionResponse> {
    const body: KbCreateSessionRequest = { mode };
    return request<KbCreateSessionResponse>(this.config, '/sessions', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** Ask a question within an existing session. */
  sendMessage(req: KbChatRequest): Promise<KbChatResponse> {
    return request<KbChatResponse>(this.config, '/chat', {
      method: 'POST',
      body: JSON.stringify(req),
    });
  }

  /** Close a session (idempotent, best-effort on the caller's part). */
  deleteSession(sessionId: string): Promise<void> {
    return request<void>(this.config, `/sessions/${sessionId}`, { method: 'DELETE' });
  }
}
